import type { OnboardingState } from '@/state/onboarding/useOnboarding';
import {
  useCheckoutReturn,
  useCheckoutWaitedTooLong,
  useIsActivatingCheckout,
} from '@/state/billing/useCheckoutReturn';
import { trackUmami } from '@/lib/umami/umami';
import { accountTypeOf } from '@/lib/umami/umami-events';
import { ActivatingStep } from './ActivatingStep';
import { ConnectWebsiteStep } from './ConnectWebsiteStep';
import { PlanStep } from './PlanStep';
import { WaitingStep } from './WaitingStep';
import { onboardingSteps } from './steps';

type OnboardingScreenProps = { onboarding: OnboardingState };

/**
 * Everything a new account sees until it is set up: no sidebar, no empty
 * dashboards, one step at a time. Which step is decided by the server
 * (decideOnboarding); this only renders it. Each step is a full page of its own
 * (the white auth desk); a Member, who cannot take either step, waits on a plain one.
 *
 * It mounts useCheckoutReturn itself. Polar sends the customer back to the billing
 * page, and with no dashboard layout there is no banner running the poll that
 * waits for the subscription to be confirmed.
 */
export const OnboardingScreen = ({ onboarding }: OnboardingScreenProps) => {
  useCheckoutReturn({
    // Here and not in the hook: the dashboard's banner runs it too, for an agency buying a
    // client's plan, which is not a new account's trial.
    onConfirmed: () => trackUmami('trial-started', { type: accountTypeOf(onboarding.isAgency) }),
  });
  const isActivating = useIsActivatingCheckout();
  const waitedTooLong = useCheckoutWaitedTooLong();

  // Paid at Polar but not yet confirmed here: wait, do not offer the plan again.
  if (onboarding.canAct && onboarding.step === 'plan' && (isActivating || waitedTooLong)) {
    return <ActivatingStep waitedTooLong={waitedTooLong && !isActivating} />;
  }

  if (onboarding.canAct && onboarding.step === 'plan') {
    return (
      <PlanStep isAgency={onboarding.isAgency} stepCount={onboardingSteps({ isAgency: onboarding.isAgency }).length} />
    );
  }

  if (onboarding.canAct && onboarding.step === 'website') return <ConnectWebsiteStep isAgency={onboarding.isAgency} />;

  return (
    <div className="bg-background min-h-screen w-full px-4 py-8 md:px-8">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
        <header className="flex items-center justify-between gap-4">
          <span className="text-foreground text-title font-medium tracking-tight">Pertento</span>
          <a
            href="/auth/signout"
            className="text-dim hover:text-foreground text-body underline-offset-4 hover:underline"
          >
            Sign out
          </a>
        </header>

        <main className="bg-panel border-line rounded-panel border p-6 md:p-10">
          <WaitingStep />
        </main>
      </div>
    </div>
  );
};
